/**
 * Item 111 — pure rules for the lead detail page and the sign-in return path.
 * Section lists mirror the item 113 SELECT policies on each table.
 */

export type SessionMatch =
  | { by: "visit"; cookieId: string }
  | { by: "email"; email: string }
  | null;

/** Cookie id first; email only when the lead has no cookie id. */
export function sessionMatchFor(lead: { visitor_cookie_id?: string | null; email?: string | null }): SessionMatch {
  const cookie = (lead.visitor_cookie_id ?? "").trim();
  if (cookie) return { by: "visit", cookieId: cookie };
  const email = (lead.email ?? "").trim().toLowerCase();
  if (email) return { by: "email", email };
  return null;
}

export const SESSION_SECTIONS = ["visitorprofiles", "visitor_journeys", "tanya_analytics", "dynamic_mode_analytics", "leads"] as const;
export const TRANSCRIPT_SECTIONS = ["tanya_analytics", "leads"] as const;
export const EVENT_SECTIONS = ["visitor_journeys", "tanya_analytics", "dynamic_mode_analytics", "campaign_analytics"] as const;

export interface LeadDetailAccess {
  sessions: boolean;
  transcript: boolean;
  journey: boolean;
  emailClicks: boolean;
  /** Label for the "Needs <x> access" note. */
  transcriptNeeds: string;
  journeyNeeds: string;
}

export function leadDetailAccess(canView: (section: string) => boolean): LeadDetailAccess {
  const sessions = SESSION_SECTIONS.some(canView);
  const transcript = sessions && TRANSCRIPT_SECTIONS.some(canView);
  const events = sessions && EVENT_SECTIONS.some(canView);
  return {
    sessions,
    transcript,
    journey: events,
    emailClicks: events,
    transcriptNeeds: "Tanya Chats",
    journeyNeeds: "Visitor Journeys",
  };
}

/** Same-origin path only. Rejects absolute URLs, `//host`, `/\host`, control characters and the login page itself. */
export function sanitiseNextPath(raw: string | null | undefined): string {
  if (!raw || typeof raw !== "string") return "";
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "";
  if (/[\u0000-\u001f\\]/.test(raw)) return "";
  try {
    const base = "https://same.invalid";
    const u = new URL(raw, base);
    if (u.origin !== base) return "";
    if (u.pathname === "/admin/login") return "";
    return u.pathname + u.search + u.hash;
  } catch {
    return "";
  }
}

export interface JourneyEvent {
  event_type: string;
  page_url: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** Groups events by UTC day, newest day first, events oldest-first within a day. */
export function groupEventsByDay<T extends { created_at: string }>(events: T[]): { day: string; events: T[] }[] {
  const map = new Map<string, T[]>();
  [...events]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .forEach((e) => {
      const day = e.created_at.slice(0, 10);
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(e);
    });
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([day, evs]) => ({ day, events: evs }));
}
